
import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import FeedbackRepository from '@/repositories/FeedbackRepository';
import { FeedbackRequest, FeedbackCategory } from '@/repositories/api/FeedbackApi';

const MAX_VIDEO_SIZE_IN_BYTES = 10 * 1024 * 1024;
const MAX_EMAIL_LENGTH = 255;
const MAX_CONCEPT_LENGTH = 255;
const MAX_DESCRIPTION_LENGTH = 5000;
const ALLOWED_VIDEO_EXTENSIONS = ['mp4', 'avi', 'mov'];
const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/x-msvideo', 'video/avi', 'video/quicktime'];
const EMAIL_PATTERN = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

@Component
export default class FeedbackFormDialog extends Vue {
    @Prop({ default: false }) public readonly isOpen!: boolean;
    @Prop({ default: null }) public readonly initialCategory!: FeedbackCategory | null;

    @Inject() private readonly feedbackRepository!: FeedbackRepository;

    // Expose enum to template
    public FeedbackCategory = FeedbackCategory;
    public readonly MAX_EMAIL_LENGTH = MAX_EMAIL_LENGTH;
    public readonly MAX_CONCEPT_LENGTH = MAX_CONCEPT_LENGTH;
    public readonly MAX_DESCRIPTION_LENGTH = MAX_DESCRIPTION_LENGTH;

    public email: string = '';
    public selectedCategory: FeedbackCategory | null = null;
    public concept: string = '';
    public selectedVideo: File | null = null;
    public description: string = '';

    public emailError: string = '';
    public categoryError: string = '';
    public conceptError: string = '';
    public videoError: string = '';
    public descriptionError: string = '';
    public submitError: string = '';

    public currentStep: number = 1;
    public isSubmitting: boolean = false;
    public showThankYou: boolean = false;

    public created(): void {
        if (this.initialCategory !== null) {
            this.selectedCategory = this.initialCategory;
        }
    }

    @Watch('initialCategory')
    public onInitialCategoryChanged(value: FeedbackCategory | null): void {
        if (value !== null && this.currentStep === 1) {
            this.selectedCategory = value;
            this.categoryError = '';
        }
    }

    public get isStepOneValid(): boolean {
        return this.isEmailValid() && !!this.selectedCategory;
    }

    public get isStepTwoValid(): boolean {
        if (!this.email || !this.selectedCategory) {
            return false;
        }
        if (this.selectedCategory === FeedbackCategory.NOT_FOUND) {
            return this.isConceptValid();
        }
        if (this.selectedCategory === FeedbackCategory.DIFFERENT) {
            return this.selectedVideo !== null && !this.videoError;
        }
        if (this.selectedCategory === FeedbackCategory.OTHER) {
            return this.isDescriptionValid();
        }
        return false;
    }

    public get hasValidSelectedVideo(): boolean {
        return this.selectedVideo !== null && !this.videoError;
    }

    public validateEmail(): void {
        if (!this.email.trim()) {
            this.emailError = (this as any).$t('feedback.email-required');
        } else if (this.email.length > MAX_EMAIL_LENGTH) {
            this.emailError = (this as any).$t('feedback.email-too-long', { max: MAX_EMAIL_LENGTH });
        } else if (!EMAIL_PATTERN.test(this.email)) {
            this.emailError = (this as any).$t('feedback.email-invalid');
        } else {
            this.emailError = '';
        }
    }

    public validateCategory(): void {
        this.categoryError = this.selectedCategory ? '' : (this as any).$t('feedback.category-required');
    }

    public validateConcept(): void {
        if (this.selectedCategory !== FeedbackCategory.NOT_FOUND) {
            this.conceptError = '';
            return;
        }

        if (!this.concept.trim()) {
            this.conceptError = (this as any).$t('feedback.concept-required');
        } else if (this.concept.length > MAX_CONCEPT_LENGTH) {
            this.conceptError = (this as any).$t('feedback.concept-too-long', { max: MAX_CONCEPT_LENGTH });
        } else {
            this.conceptError = '';
        }
    }

    public validateVideo(): void {
        if (this.selectedCategory !== FeedbackCategory.DIFFERENT) {
            this.videoError = '';
            return;
        }

        if (!this.selectedVideo) {
            this.videoError = (this as any).$t('feedback.video-required');
            return;
        }

        if (!this.isAllowedVideoFile(this.selectedVideo)) {
            this.videoError = (this as any).$t('feedback.upload-format-error');
            return;
        }

        if (this.selectedVideo.size > MAX_VIDEO_SIZE_IN_BYTES) {
            this.videoError = (this as any).$t('feedback.upload-size-error');
            return;
        }

        this.videoError = '';
    }

    public validateDescription(): void {
        if (this.selectedCategory !== FeedbackCategory.OTHER) {
            this.descriptionError = '';
            return;
        }

        if (!this.description.trim()) {
            this.descriptionError = (this as any).$t('feedback.description-required');
        } else if (this.description.length > MAX_DESCRIPTION_LENGTH) {
            this.descriptionError = (this as any).$t('feedback.description-too-long', { max: MAX_DESCRIPTION_LENGTH });
        } else {
            this.descriptionError = '';
        }
    }

    public onCategoryChanged(): void {
        this.clearSubmitError();
        this.categoryError = '';
        this.concept = '';
        this.clearSelectedVideo();
        this.description = '';
        this.conceptError = '';
        this.videoError = '';
        this.descriptionError = '';
    }

    public onNextStep(): void {
        this.clearSubmitError();
        this.validateEmail();
        this.validateCategory();
        if (this.isStepOneValid) {
            this.currentStep = 2;
        }
    }

    public onPreviousStep(): void {
        this.clearSubmitError();
        this.currentStep = 1;
    }

    public onVideoSelected(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
            this.setSelectedVideo(target.files[0]);
        }
    }

    public onVideoDropped(event: DragEvent): void {
        if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            this.setSelectedVideo(event.dataTransfer.files[0]);
        }
    }

    public onClearSelectedVideo(): void {
        this.clearSelectedVideo();
    }

    public onSend(): void {
        if (this.currentStep !== 2) {
            return;
        }

        this.clearSubmitError();
        this.validateConcept();
        this.validateVideo();
        this.validateDescription();
        if (!this.isStepTwoValid) {
            return;
        }

        this.isSubmitting = true;
        const feedback: FeedbackRequest = {
            email: this.email,
            category: this.selectedCategory as FeedbackCategory,
        };

        if (this.selectedCategory === FeedbackCategory.NOT_FOUND) {
            feedback.concept = this.concept;
        } else if (this.selectedCategory === FeedbackCategory.DIFFERENT) {
            feedback.videoFile = this.selectedVideo || undefined;
        } else if (this.selectedCategory === FeedbackCategory.OTHER) {
            feedback.description = this.description;
        }

        this.feedbackRepository.submitFeedback(feedback).subscribe(
            response => {
                this.isSubmitting = false;
                if (response && !response.success) {
                    this.submitError = (this as any).$t('feedback.error-sending');
                    this.$emit('error', this.submitError);
                } else {
                    this.showThankYou = true;
                }
            },
            () => {
                this.isSubmitting = false;
                this.submitError = (this as any).$t('feedback.error-sending');
                this.$emit('error', this.submitError);
            },
        );
    }

    public onCancel(): void {
        if (this.showThankYou) {
            // Reset form after closing the thank-you page using close icon button.
            this.resetForm();
        }
        this.$emit('close');
    }

    public onClose(): void {
        this.resetForm();
        this.$emit('close');
    }

    public beforeDestroy(): void {
        this.resetForm();
    }

    private resetForm(): void {
        this.email = '';
        this.selectedCategory = null;
        this.concept = '';
        this.clearSelectedVideo();
        this.description = '';
        this.emailError = '';
        this.categoryError = '';
        this.conceptError = '';
        this.videoError = '';
        this.descriptionError = '';
        this.submitError = '';
        this.currentStep = 1;
        this.isSubmitting = false;
        this.showThankYou = false;

        if (this.initialCategory !== null) {
            this.selectedCategory = this.initialCategory;
        }
    }

    private clearSelectedVideo(): void {
        this.selectedVideo = null;
        this.videoError = '';
        const input = this.$refs.feedbackVideoInput as HTMLInputElement | undefined;
        if (input) {
            input.value = '';
        }
    }

    private setSelectedVideo(file: File): void {
        this.selectedVideo = file;
        this.validateVideo();

        const input = this.$refs.feedbackVideoInput as HTMLInputElement | undefined;
        if (input) {
            input.value = '';
        }
    }

    private isAllowedVideoFile(file: File): boolean {
        const fileName = file.name.toLowerCase();
        const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
        const hasAllowedExtension = !!extension && ALLOWED_VIDEO_EXTENSIONS.includes(extension);
        const normalizedMimeType = file.type.toLowerCase();
        const hasAllowedMimeType = !normalizedMimeType || ALLOWED_VIDEO_MIME_TYPES.includes(normalizedMimeType);

        return hasAllowedExtension && hasAllowedMimeType;
    }

    private clearSubmitError(): void {
        this.submitError = '';
    }

    private isEmailValid(): boolean {
        return !!this.email.trim() && this.email.length <= MAX_EMAIL_LENGTH && EMAIL_PATTERN.test(this.email);
    }

    private isConceptValid(): boolean {
        return this.selectedCategory === FeedbackCategory.NOT_FOUND
            ? !!this.concept.trim() && this.concept.length <= MAX_CONCEPT_LENGTH
            : false;
    }

    private isDescriptionValid(): boolean {
        return this.selectedCategory === FeedbackCategory.OTHER
            ? !!this.description.trim() && this.description.length <= MAX_DESCRIPTION_LENGTH
            : false;
    }
}
